import { Component, type ComponentType, type ReactNode } from "react";
import { withTranslation, type WithTranslation } from "react-i18next";
import styles from "./ChunkErrorBoundary.module.css";

const RELOAD_TIMESTAMP_KEY = "lb:chunk-error-reload-at";
const RELOAD_COOLDOWN_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return /dynamically imported module|Importing a module script failed|Failed to fetch|MIME type/i.test(
    message,
  );
}

function shouldAutoReload(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) ?? 0);
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return false;
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

interface Props extends WithTranslation {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

/**
 * Catches route-chunk load failures. These happen when a tab stays open
 * across a deploy and then lazy-loads a route whose hashed file no longer
 * exists on the server — reload once to pick up the current build instead
 * of leaving the user on a dead page.
 */
class ChunkErrorBoundaryImpl extends Component<Props, State> {
  state: State = { hasError: false, isChunkError: false };

  static getDerivedStateFromError(error: unknown): State {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error: unknown) {
    if (isChunkLoadError(error) && shouldAutoReload()) {
      window.location.reload();
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) return null;
      const { t } = this.props;
      return (
        <div className={styles.center} role="alert">
          <p>{t("common.error")}</p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => window.location.reload()}
          >
            {t("common.retry")}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

const ChunkErrorBoundary: ComponentType<{ children: ReactNode }> =
  withTranslation()(ChunkErrorBoundaryImpl);

export default ChunkErrorBoundary;
